/**
 * Demo achats (purchases) — mix of VALIDE, BROUILLON and ANNULE pieces from
 * the grossistes + laboratoires fixtures. Totals are coherent (HT/TVA/TTC)
 * so the comptabilité dashboard and balance fournisseur show real numbers.
 */
import { nowIso, dateOffsetYmd } from "../uuid.js";

export function buildAchats(fournisseurs, produits, _stockLots, users) {
  const frnById = Object.fromEntries((fournisseurs || []).map((f) => [f.id, f]));
  const prodById = Object.fromEntries((produits || []).map((p) => [p.id, p]));
  const usrById = Object.fromEntries((users || []).map((u) => [u.id, u]));
  const proprietaire = usrById["beta-user-proprietaire"]?.id || null;
  const manager = usrById["beta-user-manager"]?.id || null;

  const records = [
    mkAchat({
      id: "achat-001", numero: "ACH-2026-001", frn: frnById["frn-001"],
      type_piece: "FACTURE", numero_piece: "F-2026-0142",
      offset_facture: -38, offset_reception: -37, offset_echeance: -8,
      statut: "VALIDE", statut_paiement: "PAYE",
      notes: "Commande mensuelle — chronique HTA / Diabète.",
      saisi_par_id: manager, validee_par_id: proprietaire,
      lines: [
        ln(prodById["p-006"], "TAH-26-A", 60, 6, 6.0),
        ln(prodById["p-015"], "COV-26-A", 80, 8, 5.0),
        ln(prodById["p-017"], "CRE-26-A", 40, 4, 6.0),
        ln(prodById["p-033"], "ELI-26-A", 30, 3, 7.0),
      ],
    }),
    mkAchat({
      id: "achat-002", numero: "ACH-2026-002", frn: frnById["frn-002"],
      type_piece: "FACTURE", numero_piece: "F-GRP-26-0883",
      offset_facture: -32, offset_reception: -31, offset_echeance: 13,
      statut: "VALIDE", statut_paiement: "PARTIEL",
      notes: "Antibiotiques + sirops — saison hivernale.",
      saisi_par_id: manager, validee_par_id: manager,
      paye_ratio: 0.55,
      lines: [
        ln(prodById["p-011"], "AUG-26-B", 50, 5, 5.0),
        ln(prodById["p-019"], "AUS-26-B", 40, 4, 4.0),
        ln(prodById["p-025"], "BIS-26-B", 60, 6, 3.0),
        ln(prodById["p-027"], "MUC-26-B", 50, 0, 4.0),
      ],
    }),
    mkAchat({
      id: "achat-003", numero: "ACH-2026-003", frn: frnById["frn-003"],
      type_piece: "FACTURE", numero_piece: "SAI-2026/00417",
      offset_facture: -25, offset_reception: -24, offset_echeance: -24,
      statut: "VALIDE", statut_paiement: "PAYE",
      notes: "Saidal — réassort génériques.",
      saisi_par_id: proprietaire, validee_par_id: proprietaire,
      lines: [
        ln(prodById["p-003"], "AMO-26-S", 100, 10, 8.0),
        ln(prodById["p-012"], "LIS-26-S", 50, 5, 6.0),
        ln(prodById["p-901"], "DIA-26-S", 20, 0, 0),
      ],
    }),
    mkAchat({
      id: "achat-004", numero: "ACH-2026-004", frn: frnById["frn-004"],
      type_piece: "FACTURE", numero_piece: "BIO-26-1124",
      offset_facture: -19, offset_reception: -18, offset_echeance: 11,
      statut: "VALIDE", statut_paiement: "NON_PAYE",
      notes: "Biopharm — gamme cardio + dermato.",
      saisi_par_id: manager, validee_par_id: proprietaire,
      lines: [
        ln(prodById["p-040"], "VAS-26-D", 60, 6, 5.0),
        ln(prodById["p-041"], "DAF-26-D", 50, 5, 4.0),
        ln(prodById["p-005"], "GLU-26-D", 80, 8, 6.0),
      ],
    }),
    mkAchat({
      id: "achat-005", numero: "ACH-2026-005", frn: frnById["frn-006"],
      type_piece: "BON_LIVRAISON", numero_piece: "BL-EK-26-0287",
      offset_facture: -14, offset_reception: -13, offset_echeance: 31,
      statut: "VALIDE", statut_paiement: "PARTIEL",
      notes: "El Kendi — para + lait infantile.",
      saisi_par_id: manager, validee_par_id: manager,
      paye_ratio: 0.30,
      lines: [
        ln(prodById["p-205"], "PRB-26-K", 40, 4, 5.0),
        ln(prodById["p-206"], "LAI-26-K", 30, 3, 4.0),
        ln(prodById["p-207"], "ARG-26-K", 25, 0, 6.0),
        ln(prodById["p-045"], "FOR-26-K", 60, 6, 5.0),
        ln(prodById["p-022"], "SME-26-K", 80, 8, 4.0),
      ],
    }),
    mkAchat({
      id: "achat-006", numero: "ACH-2026-006", frn: frnById["frn-007"],
      type_piece: "FACTURE", numero_piece: "MER-2026-0334",
      offset_facture: -9, offset_reception: -8, offset_echeance: -8,
      statut: "VALIDE", statut_paiement: "PAYE",
      notes: "Merinal — comptant.",
      saisi_par_id: proprietaire, validee_par_id: proprietaire,
      lines: [
        ln(prodById["p-036"], "NEX-26-M", 50, 5, 4.0),
        ln(prodById["p-200"], "BET-26-M", 30, 3, 5.0),
        ln(prodById["p-300"], "COM-26-M", 40, 4, 4.0),
      ],
    }),
    mkAchat({
      id: "achat-007", numero: "ACH-2026-007", frn: frnById["frn-001"],
      type_piece: "FACTURE", numero_piece: "F-2026-0188",
      offset_facture: -6, offset_reception: -5, offset_echeance: 24,
      statut: "BROUILLON", statut_paiement: "NON_PAYE",
      notes: "À valider — vérification prix HT en cours.",
      saisi_par_id: manager, validee_par_id: null,
      lines: [
        ln(prodById["p-034"], "XAR-26-X", 25, 2, 7.0),
        ln(prodById["p-044"], "LEV-26-X", 20, 2, 6.0),
      ],
    }),
    mkAchat({
      id: "achat-008", numero: "ACH-2026-008", frn: frnById["frn-005"],
      type_piece: "BON_LIVRAISON", numero_piece: "BL-POW-26-0091",
      offset_facture: -4, offset_reception: -4, offset_echeance: 56,
      statut: "BROUILLON", statut_paiement: "NON_PAYE",
      notes: "Réception partielle — manquants à signaler.",
      saisi_par_id: manager, validee_par_id: null,
      lines: [
        ln(prodById["p-302"], "SER-26-O", 20, 2, 5.0),
        ln(prodById["p-304"], "THE-26-O", 15, 0, 4.0),
        ln(prodById["p-202"], "VTC-26-O", 30, 3, 6.0),
      ],
    }),
    mkAchat({
      id: "achat-009", numero: "ACH-2026-009", frn: frnById["frn-002"],
      type_piece: "FACTURE", numero_piece: "F-GRP-26-0901",
      offset_facture: -3, offset_reception: -2, offset_echeance: 43,
      statut: "BROUILLON", statut_paiement: "NON_PAYE",
      notes: "Brouillon — en attente du justificatif fournisseur.",
      saisi_par_id: manager, validee_par_id: null,
      lines: [
        ln(prodById["p-008"], "MOP-26-J", 40, 4, 5.0),
        ln(prodById["p-035"], "INE-26-J", 30, 3, 6.0),
      ],
    }),
    mkAchat({
      id: "achat-010", numero: "ACH-2026-010", frn: frnById["frn-008"],
      type_piece: "FACTURE", numero_piece: "HYD-26-0042",
      offset_facture: -21, offset_reception: -20, offset_echeance: -5,
      statut: "ANNULE", statut_paiement: "NON_PAYE",
      notes: "Annulé — erreur de fournisseur, doublon avec achat-003.",
      saisi_par_id: manager, validee_par_id: proprietaire,
      lines: [
        ln(prodById["p-003"], "AMO-DUP", 40, 0, 0),
        ln(prodById["p-009"], "VOL-DUP", 30, 0, 0),
      ],
    }),
    mkAchat({
      id: "achat-011", numero: "ACH-2026-011", frn: frnById["frn-003"],
      type_piece: "FACTURE", numero_piece: "SAI-2026/00482",
      offset_facture: -1, offset_reception: 0, offset_echeance: 0,
      statut: "VALIDE", statut_paiement: "PAYE",
      notes: "Saidal — règlement comptant à la livraison.",
      saisi_par_id: proprietaire, validee_par_id: proprietaire,
      lines: [
        ln(prodById["p-001"], "DOL-26-Z", 120, 12, 4.0),
        ln(prodById["p-002"], "DOS-26-Z", 100, 10, 4.0),
        ln(prodById["p-010"], "ASP-26-Z", 80, 8, 3.0),
        ln(prodById["p-020"], "SPA-26-Z", 60, 6, 3.0),
      ],
    }),
    mkAchat({
      id: "achat-012", numero: "ACH-2026-012", frn: frnById["frn-004"],
      type_piece: "FACTURE", numero_piece: "BIO-26-1187",
      offset_facture: 0, offset_reception: 0, offset_echeance: 30,
      statut: "VALIDE", statut_paiement: "PAYE",
      notes: "Biopharm — règlement immédiat par virement.",
      saisi_par_id: proprietaire, validee_par_id: proprietaire,
      lines: [
        ln(prodById["p-042"], "INS-26-N", 15, 0, 5.0),
        ln(prodById["p-043"], "LAN-26-N", 12, 0, 5.0),
        ln(prodById["p-007"], "VEN-26-N", 30, 3, 6.0),
      ],
    }),
  ];

  return records;
}

// ── helpers ────────────────────────────────────────────────────────────────
function ln(produit, numero_lot, quantite, quantite_offerte, remise_pct) {
  if (!produit) return null;
  const tva_pct = produit.tva_pct ?? (produit.is_remboursable ? 0 : 19);
  const prix_achat_ht = round2((produit.ppa || 100) * 0.55);
  const prix_achat_ttc = round2(prix_achat_ht * (1 + tva_pct / 100));
  const brut_ht = prix_achat_ht * quantite;
  const apres_remise_ht = brut_ht * (1 - (remise_pct || 0) / 100);
  const montant_ligne_ht = round2(apres_remise_ht);
  const montant_ligne_ttc = round2(apres_remise_ht * (1 + tva_pct / 100));
  return {
    produit_id: produit.id,
    produit: {
      id: produit.id,
      nom_commercial: produit.nom_commercial,
      ppa: produit.ppa,
      code_barre: produit.code_barre,
      dci: produit.dci,
      forme_pharma: produit.forme_pharma,
      dosage: produit.dosage,
      laboratoire_id: produit.laboratoire_id,
    },
    numero_lot,
    date_peremption: dateOffsetYmd(360),
    quantite,
    quantite_offerte: quantite_offerte || 0,
    prix_achat_ht,
    prix_achat_ttc,
    remise_pct: remise_pct || 0,
    tva_pct,
    montant_ligne_ht,
    montant_ligne_ttc,
  };
}

function mkAchat(cfg) {
  const lines = (cfg.lines || []).filter(Boolean).map((l, i) => ({
    id: `${cfg.id}-l${i + 1}`,
    ...l,
  }));
  const montant_ht = round2(lines.reduce((s, l) => s + l.montant_ligne_ht, 0));
  const montant_tva = round2(
    lines.reduce(
      (s, l) => s + (l.montant_ligne_ht * (l.tva_pct || 0)) / 100,
      0,
    ),
  );
  const montant_ttc = round2(montant_ht + montant_tva);
  let montant_paye = 0;
  if (cfg.statut_paiement === "PAYE") montant_paye = montant_ttc;
  else if (cfg.statut_paiement === "PARTIEL") {
    montant_paye = round2(montant_ttc * (cfg.paye_ratio ?? 0.5));
  }
  const reste_a_payer = round2(montant_ttc - montant_paye);
  return {
    id: cfg.id,
    numero_interne: cfg.numero,
    fournisseur_id: cfg.frn?.id || null,
    fournisseur: cfg.frn ? { id: cfg.frn.id, nom: cfg.frn.nom } : null,
    type_piece: cfg.type_piece,
    numero_piece: cfg.numero_piece,
    date_facture: dateOffsetYmd(cfg.offset_facture),
    date_reception: dateOffsetYmd(cfg.offset_reception),
    date_echeance: dateOffsetYmd(cfg.offset_echeance),
    statut: cfg.statut,
    montant_ht,
    montant_tva,
    montant_ttc,
    montant_paye,
    reste_a_payer,
    statut_paiement: cfg.statut_paiement,
    notes: cfg.notes || "",
    saisi_par_id: cfg.saisi_par_id || null,
    validee_par_id: cfg.validee_par_id || null,
    details: lines,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
